import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Check, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Force-change-password page.
//
// • Reached automatically post-login when user.must_change_password === true
//   (enforced by the ForcePasswordChange guard in App.js).
// • Also reachable from a user's own account menu for self-serve rotations.
//
// On success the AuthContext re-fetches /auth/me so the must_change_password
// flag clears and the guard stops redirecting.
export default function ChangePassword() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const isForced = !!user?.must_change_password;
  const next = location.state?.next || (user?.role === "editor" ? "/admin/insights" : "/dashboard");

  useEffect(() => {
    if (user === null) return; // still loading
    if (!user) navigate("/login", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPw.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (newPw !== confirm) {
      setError("New passwords don't match.");
      return;
    }
    if (newPw === currentPw) {
      setError("New password must be different from your current one.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPw,
        new_password: newPw,
      });
      // Re-fetch /auth/me so must_change_password clears in context
      if (typeof refreshUser === "function") await refreshUser();
      setDone(true);
      setTimeout(() => navigate(next, { replace: true }), 1800);
    } catch (err) {
      setError(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Couldn't update password — please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (user === null || !user) {
    return <div className="min-h-screen flex items-center justify-center text-ink-soft">Verifying…</div>;
  }

  return (
    <div className="min-h-screen bg-cream flex" data-testid="page-change-password">
      <div className="hidden lg:block lg:w-1/2 bg-sage relative">
        <div className="absolute inset-0 flex items-center justify-center p-16">
          <div className="text-cream max-w-md">
            <div className="inline-flex items-center gap-2 bg-cream/15 backdrop-blur-sm border border-cream/20 rounded-full px-3 py-1 text-[0.65rem] tracking-[0.18em] uppercase font-semibold mb-6">
              <ShieldCheck size={11} strokeWidth={2.4} /> Secure access
            </div>
            <div className="font-serif-display text-5xl leading-[1.05]">
              {isForced ? "Set your own password." : "Change your password."}
            </div>
            <p className="mt-6 text-cream/75 leading-relaxed">
              {isForced
                ? "You're signed in with a temporary password. Choose something only you know — at least 8 characters — and we'll take you to your work."
                : "Pick a new password that's only known to you. At least 8 characters."}
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="font-serif-display text-2xl text-ink" data-testid="change-pw-logo">
            Pip<span className="text-gold italic"> Bennett</span>
          </Link>

          {done ? (
            <div className="mt-12" data-testid="change-pw-success">
              <div className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-sage text-cream mb-6">
                <Check size={20} strokeWidth={1.5} />
              </div>
              <h1 className="font-serif-display text-4xl text-ink leading-tight">Password updated.</h1>
              <p className="mt-5 text-ink-soft leading-relaxed">Taking you to your dashboard…</p>
            </div>
          ) : (
            <>
              {isForced && (
                <div
                  className="mt-10 mb-2 inline-flex items-start gap-2 bg-gold/10 border border-gold/40 text-ink rounded-2xl px-4 py-3 text-xs leading-relaxed max-w-md"
                  data-testid="change-pw-forced-notice"
                >
                  <ShieldCheck size={14} strokeWidth={2} className="text-gold-dark flex-shrink-0 mt-0.5" />
                  <span>
                    For security, please set your own password before continuing. Your account was created with a
                    temporary password.
                  </span>
                </div>
              )}
              <h1 className="font-serif-display text-4xl text-ink mt-6 leading-tight">
                {isForced ? "Set your password." : "Change password."}
              </h1>
              <p className="mt-3 text-ink-soft">Signed in as {user.email}.</p>

              <form onSubmit={submit} className="mt-10 space-y-5" data-testid="change-pw-form">
                <div>
                  <label className="eyebrow block mb-2 text-ink-soft">
                    {isForced ? "Temporary password" : "Current password"}
                  </label>
                  <input
                    required
                    type="password"
                    autoComplete="current-password"
                    value={currentPw}
                    onChange={(e) => setCurrentPw(e.target.value)}
                    className="w-full bg-white border border-hairline rounded-full px-6 py-4 text-ink focus:outline-none focus:border-sage transition"
                    data-testid="change-pw-current"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2 text-ink-soft">New password</label>
                  <input
                    required
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    className="w-full bg-white border border-hairline rounded-full px-6 py-4 text-ink focus:outline-none focus:border-sage transition"
                    data-testid="change-pw-new"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2 text-ink-soft">Confirm new password</label>
                  <input
                    required
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full bg-white border border-hairline rounded-full px-6 py-4 text-ink focus:outline-none focus:border-sage transition"
                    data-testid="change-pw-confirm"
                  />
                </div>
                {error && (
                  <div
                    className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3"
                    data-testid="change-pw-error"
                  >
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-sage hover:bg-sage-dark text-cream rounded-full px-8 py-4 font-medium tracking-wide transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="change-pw-submit"
                >
                  {loading ? "Saving…" : "Save new password"}
                  {!loading && <ArrowRight size={16} strokeWidth={1.8} />}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
